import { FC, useState, useEffect, useCallback, FormEvent, useRef } from "react";
import { Document, Project, UserModel } from "../../../models";
import { HorizontalStack, VerticalStack } from "../../Stack";
import { ProjectCard } from "../ProjectCard";
import {
  IStackTokens,
  IStackStyles,
  Text,
  Stack,
  IPlainCardProps,
  HoverCard,
  HoverCardType,
  IStackProps,
  TextField,
  IStyleFunctionOrObject,
  ITextFieldStyleProps,
  ITextFieldStyles,
  FontIcon,
  TooltipHost,
} from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { colorPallete } from "../../../assets/color";
import {
  getColor,
  getFormattedDate,
  getSortedMRU,
} from "../../../utils";
import Popup from "../../Popup/Popup";
import SuccessIcon from "../../../assets/Icons/svg/successIcon";
import TrashIcon from "../../../assets/Icons/svg/trashIcon";
import useModal from "../../../hooks/useModal";
import DeleteProjectModal from "../../modals/DeleteProjectModal";
import { EditProjectModal } from "../../modals/EditProjectModal";
import { ProjectService } from "../../../services/ProjectService";
import { FormModal } from "../../modals/Modal";
import EditIcon from "../../../assets/Icons/svg/editIcon";
import { useAppDispatch } from "../../../hooks/hooks";
import { getAllProjects, getAllProjectDetails } from "../../../modules/redux/Projects/project";
import { useParams } from "react-router-dom";

const innerStackTokens: IStackTokens = {
  childrenGap: "2%",
};

const outerStackStyles: IStackStyles = {
  root: {
    paddingLeft: "4%",
    paddingRight: "4%",
  },
};

type Props = {
  user?: UserModel;
  project: Project;
  onClick?: () => void;
  sender?: string;
  disableValueEdit?: boolean;
  isOwner?: boolean;
  projectOwner?: string;
  projectStatus?: string;
};

const CounterPartiesHoverCard: React.FC<{ counterParties: string[] }> = ({
  counterParties,
}) => {
  const plainCardProps: IPlainCardProps = {
    onRenderPlainCard: () => (
      <div style={{ padding: "5px", backgroundColor: "white" }}>
        <ul style={{ listStyleType: "none" }}>
          {counterParties.map((counterParty) => (
            <li key={counterParty}>{counterParty}</li>
          ))}
        </ul>
      </div>
    ),

    directionalHint: 1,
  };

  return (
    <HoverCard type={HoverCardType.plain} plainCardProps={plainCardProps}>
      <div
        style={{
          fontSize: "14px",
          borderRadius: "1rem",
          minWidth: "1px",
          maxWidth: "fit-content",
          color: "rgb(6 42 108)",
        }}
      >
        {counterParties.length === 1
          ? `+${counterParties.length} Other`
          : `+${counterParties.length} Others`}
      </div>
    </HoverCard>
  );
};

const TruncatedTextWithTooltip: React.FC<{ text: string; style: any }> = ({
  text,
  style,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        const truncated = element.scrollWidth > element.clientWidth;
        setIsTruncated(truncated);
      }
    };

    let rafId1: number;
    let rafId2: number;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        checkTruncation();
      });
    });
    
    const timeoutId = setTimeout(checkTruncation, 100);
    window.addEventListener("resize", checkTruncation);
    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text]);

  const textElement = (
    <div ref={textRef} style={style}>
      {text}
    </div>
  );

  if (isTruncated) {
    return <TooltipHost content={text}>{textElement}</TooltipHost>;
  }

  return textElement;
};

export const ProjectInCard: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { isModalOpen, showModal, hideModal } = useModal();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [docData, setDocData] = useState<Document[]>([]);
  const [projectValue, setProjectValue] = useState(
    props.project?.value != null ? String(props.project.value) : "0"
  );
  const [projectStatus, setProjectStatus] = useState<string | undefined>(
    props.projectStatus || props.project?.status
  );
  const [isValueChanged, setIsValueChanged] = useState(false);
  const valueChanged = () => setIsValueChanged(true);
  const valueNotChanged = () => setIsValueChanged(false);
  const navigate = useNavigate();
  const projectId = props.project?.id?.toString();
  const dispatch = useAppDispatch();
  const { projectId: urlProjectId } = useParams();
 const refreshProjectData = useCallback(() => {
    dispatch(getAllProjects(
      {
        limit: 500,
        offset: 0,
      }
    ));
    if (urlProjectId) {
      dispatch(getAllProjectDetails(parseInt(urlProjectId, 10)));
    }
  }, [dispatch, urlProjectId]);

  useEffect(() => {
    if (props?.project?.documents && props?.project?.documents.length > 0) {
      const sortedArray = getSortedMRU(props?.project?.documents);
      setDocData(sortedArray);
    } else {
      setDocData([]);
    }
  }, [props?.project?.documents]);

  useEffect(() => {
    if (props.projectStatus) {
      setProjectStatus(props.projectStatus);
    } else if (props.project?.status) {
      setProjectStatus(props.project.status);
    }
  }, [props.projectStatus, props.project?.status]);

  // Status and value both come from the projects list (flattened at decode time),
  // so no per-project GET /projects/{id}/status call is needed here.
  useEffect(() => {
    if (props.project?.value != null) {
      setProjectValue(String(props.project.value));
    }
  }, [props.project?.value]);
  useEffect(() => {
    if (isValueChanged) {
      setTimeout(() => {
        valueNotChanged();
      }, 2000);
    }
  }, [isValueChanged]);

  useEffect(() => {
    if (isSuccessModalOpen) {
      const timer = setTimeout(() => {
        setIsSuccessModalOpen(false);
        refreshProjectData();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSuccessModalOpen, refreshProjectData]);

  const showDocuments = (projectId: number | undefined) => {
    navigate(`${projectId}`, {
      state: {
        review: false,
        fileName: null,
      },
    });
  };

  function showEditModal() {
    setModalOpen(true);
  }

  function hideEditModal() {
    setModalOpen(false);
  }

  // Updated renderPerson function
  const renderCounterParties = (data: string | undefined) => {
    let counterParties = data?.split(";");
    if (counterParties && counterParties.length > 0) {
      let remainingCount = counterParties.length - 2;
      const horizontalStackProps: IStackProps = {
        style: { width: "100%", flexWrap: "wrap" },
      };

      return (
        <Stack {...horizontalStackProps}>
          {counterParties.slice(0, 2).map((name) => {
            return (
              <HorizontalStack style={styles.counterPartyContainer} key={name}>
                <Stack
                  style={{
                    ...styles.counterPartyLogo,
                    backgroundColor: getColor(name.trim()),
                  }}
                >
                  <Text style={styles.counterPartyInitial}>
                    {name.trim().charAt(0).toUpperCase()}
                  </Text>
                </Stack>
                <Text style={styles.counterParty} title={name}>{name.trim()}</Text>
              </HorizontalStack>
            );
          })}

          {remainingCount > 0 && (
            <CounterPartiesHoverCard counterParties={counterParties.slice(2)} />
          )}
        </Stack>
      );
    }
    return null;
  };

  const [editModalKey, setEditModalKey] = useState<number>(0);
  const resetEditModal = () => {
    hideEditModal();
    setEditModalKey((prevKey) => prevKey + 1);
  };
  const NumberWithCommas = (number: string) => {
    const addCommas = (num: string) => {
      const cleanedStr = num.replace(/\D/g, "");
      return "$" + cleanedStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const formattedNumber = addCommas(number);
    return formattedNumber;
  };

  const projectValueStyles: IStyleFunctionOrObject<
    ITextFieldStyleProps,
    ITextFieldStyles
  > = {
    root: {
      backgroundColor: "#F8F8F8",
      width: "100%",
      fontSize: "16px",
      fontWeight: 400,
      fontFamily: "Poppins",
    },
    field: {
      backgroundColor: "#F8F8F8",
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px",
      fontFamily: "Poppins",
      selectors: {
        "::placeholder": {
          color: "#717070",
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "24px",
          fontFamily: "Poppins",
        },
      },
    },
  };

  const onChangeProjectValue = useCallback(
    (
      event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      setProjectValue(newValue ?? "");
    },
    []
  );

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      if (projectValue?.split("$")[1]?.length) {
        setTimeout(() => {
          valueChanged();
        }, 500);
        const cleanedValue = projectValue?.split("$")[1]?.replace(/,/g, "");
        const payload = {
          id: props.project.id,
          value: cleanedValue,
          status: projectStatus,
        };
        ProjectService.changeStatusOfProject(payload);
      }
    }
  };

  return (
    <ProjectCard>
      <div className="ProjectCard">
        <div className="Card">
          <VerticalStack
            style={{
              borderRadius: "8px",
            }}
            className="ProjectCardTop"
            onClick={() => {
              props.sender !== "AllProjects" &&
                showDocuments(props.project?.id);
            }}
          >
            <VerticalStack tokens={innerStackTokens} styles={outerStackStyles}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text className="ProjectName" title={props.project?.name}>
                  {props.project?.name}
                </Text>

                {projectStatus==="Closed" && !window.location.pathname.includes(projectId!) && (
                  <Text style={styles.closedStatus}>
                    {projectStatus}
                  </Text>
                )}
              </div>
              <Text
                title={props.project?.counterParties}
                className="CounterParty"
              >
                {t("counter_parties")}
              </Text>
              {props.project?.counterParties &&
                renderCounterParties(props.project?.counterParties)}
            </VerticalStack>
          </VerticalStack>
          <VerticalStack
            style={{
              backgroundColor: "",
              position: "absolute",
              bottom:  50,
              width: "87%",
              marginLeft: "4%",
            }}
          >
            <TextField
              styles={projectValueStyles}
              style={{
                borderRadius: "8px",
                border: "1px solid #E4E8EC",
              }}
              borderless
              inputMode="numeric"
              id="estimatedClosingValue"
              onKeyDown={handleKeyDown}
              disabled={props.disableValueEdit || !props.isOwner}
              value={
                projectValue.length === 0
                  ? "$" + String(projectValue)
                  : NumberWithCommas(String(projectValue))
              }
              onChange={onChangeProjectValue}
            />
          </VerticalStack>
          <FormModal
            title={t("success")}
            TitleBackgroundColor={"#13540C"}
            isModalOpen={isValueChanged}
            showModal={valueChanged}
            hideModal={valueNotChanged}
            width="595px"
            height={"149px"}
            cancelButton={true}
            customCancel={valueNotChanged}
          >
            <Stack
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginLeft: 15,
                marginTop: 20,
              }}
            >
              <FontIcon
                iconName="SkypeCircleCheck"
                style={{
                  fontSize: "25px",
                  marginTop: 10,
                  backgroundColor: "white",
                  color: colorPallete.btGreen,
                }}
              />
              <Text
                style={{
                  fontSize: "16px",
                  fontFamily: "Poppins",
                  paddingLeft: "15px",
                  paddingTop: "10px",
                }}
              >
                Project value is changed.
              </Text>
            </Stack>
          </FormModal>
          <HorizontalStack
            style={{
              ...styles.date,
              position: "absolute",
              width: "87%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text style={{ ...styles.date, marginLeft: 0, fontSize: "10px" }}>
              {(() => {
                const createdAt = props.project?.createdAt || (props.project as any)?.createdAt;
                const dateToUse = createdAt || props.project?.lastUpdatedDate;
                return dateToUse ? getFormattedDate(String(dateToUse)) : "";
              })()}
            </Text>
            <HorizontalStack horizontalAlign="end" style={{ width: "auto" }}>
              {props.isOwner ? (
                <HorizontalStack horizontalAlign="end" style={{ width: "auto" }}>
                  <Stack
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      showEditModal();
                    }}
                  >
                    <div className="whiteIconHover"><EditIcon /></div>
                  </Stack>
                </HorizontalStack>
              ) : null}

              {window.location.pathname.includes(projectId!) ? (
                <HorizontalStack horizontalAlign="end" style={{ width: "auto"}}>
                  <Stack
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      showModal();
                    }}
                  >
                    <div className="whiteIconHover"><TrashIcon /></div>
                  </Stack>
                </HorizontalStack>
              ) : null}
            </HorizontalStack>
          </HorizontalStack>
        </div>
      </div>
      <DeleteProjectModal
        isModalOpen={isModalOpen}
        showModal={showModal}
        hideModal={hideModal}
        projectName={props.project?.name}
        projectId={props.project?.id}
      />
      <div key={editModalKey}>
        <EditProjectModal
          isModalOpen={modalOpen}
          showModal={showEditModal}
          hideModal={resetEditModal}
          projectId={props.project?.id}
          folderName={props.project?.name}
          projectValue={projectValue}
          status={projectStatus}
          projectOwner={props.projectOwner}
          counterParties={props.project?.counterParties}
          createdDate={props.project?.createdAt}
          completionDate={props.project?.estimatedCompletionDate}
          description={props.project?.description}
          clientNumber={props.project?.clientNumber}
          clientMatter={props.project?.clientMatter}
          onSaveChanges={async (updatedData: any) => {
            try {
              const detailsResponse = await ProjectService.updateDetails(updatedData);
              const cleanedValue = updatedData.projectValue 
                ? String(updatedData.projectValue).replace(/[$,]/g, "")
                : projectValue?.split("$")[1]?.replace(/,/g, "");
              const payload = {
                id: props.project.id,
                value: cleanedValue ? parseFloat(cleanedValue) : null,
                status: updatedData.projectStatus || projectStatus,
              };
              const statusResponse = await ProjectService.changeStatusOfProject(payload);
              
              if (detailsResponse.code === 200) {
                resetEditModal();
                setIsSuccessModalOpen(true);
              }
              if (statusResponse.code === 200) {
                resetEditModal();
                setIsSuccessModalOpen(true);
              }
              return {detailsResponse, statusResponse};
            } catch (error) {
              console.error('Error updating project:', error);
              alert('Error updating project. Please try again.');
            }
          }}
        />
      </div>
      <Popup
        isOpen={isSuccessModalOpen}
        title="Success"
        iconName={<SuccessIcon />}
        contentTitle="Project Updated"
        content="Your project has been updated successfully."
        buttons={[]}
        onClose={() => {
          setIsSuccessModalOpen(false);
          refreshProjectData();
        }}
        customContent=""
        showCloseButton={true}
        width="480px"
        minHeight="175px"
        backgroundColor="linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"
      />
    </ProjectCard>
  );
};

const styles = {
  counterPartyLogo: {
    height: "32px",
    aspectRatio: 1,
    borderRadius: "16px",
    alignItems: "center",
    justifyContent: "center",
  },
  counterPartyInitial: {
    fontSize: "13px",
    lineHeight: "16px",
    color: "#F4F4F4",
  },
  counterParty: {
    paddingLeft: "4%",
    fontSize: "13px",
    lineHeight: "16px",
    fontWeight: "400",
    color: "rgb(50, 50, 50)",
    fontFamily: "Poppins",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
    whiteSpace: "normal",
  },

  counterPartyContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingBottom: "3.5%",
    paddingTop: "0.5%",
    width: "190px",
    height: "40px",
},
  bodyStyles: { paddingTop: "2%", paddingBottom: "4%", backgroundColor: "" },
  documentName: {
    fontSize: "14px",
    color: colorPallete.grey4,
    backgroundColor: "",
    textOverflow: "ellipsis",
    overflow: "hidden",
    fontWeight: 400,
    fontFamily: "Poppins",
  },
  blueText: {
    fontFamily: "Poppins",
    fontWeight: 500,
    color: "var(--primary-blue, #18468F)",
    fontSize: "12px",
    fontStyle: "normal",
  },
  date: {
    display: "flex",
    marginLeft: "4%",
    fontSize: "12px",
    color: "#717070",
    bottom: 12,
    fontFamily: "Poppins",
  },
  closedStatus: {
    display: "flex" as const, 
    justifyContent: "center" as const, 
    position: "relative" as const, 
    top: "-4px", 
    right: 0, 
    fontSize: "11px", 
    backgroundColor: colorPallete.btRed, 
    color: colorPallete.white,
    marginTop: "4px",
    padding: "2px 6px 3px 6px",
    borderRadius: "20px",
    textTransform: "uppercase" as const,
  },
};
